/* eslint-disable */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import apiConfigs from 'Api/api-config'
import { getConfigKey, prepareHeaders } from 'utility/Api'

const baseUrl = apiConfigs[getConfigKey()].apiHost
const apiPath = apiConfigs[getConfigKey()].paths.finance

interface RowData {
  '@id': any
  COST_OBJECT?: string
  NAME1_L?: string
  NAME2_L?: string
  NAME3_L?: string
  PRCTR?: string
  DESCR?: string
  KRBID?: string
  MITID?: string
}

interface Row {
  row: RowData[]
}
interface SAPResponse {
  items: Row
  item: Row
}

interface Query {
  search: string
  selectedProfitCenters: any[]
  selectedSupervisors?: any[]
  financialPeriod?: string
}

// Define a service using a base URL and expected endpoints
export const FinanceApi = createApi({
  reducerPath: 'financeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/${apiPath}`,
    prepareHeaders: async headers => await prepareHeaders(headers)
  }),
  endpoints: builder => ({
    getAllCostObjects: builder.query<any[], Query>({
      query: (query: Query) => {
        const { search, selectedProfitCenters, selectedSupervisors, financialPeriod } = query
        return {
          url: 'all-cost-objects',
          method: 'POST',
          body: {
            profitCenters: selectedProfitCenters,
            supervisors: selectedSupervisors
          },
          params: { search: search, financialPeriod: financialPeriod === 'No financial period' ? undefined : financialPeriod },
          responseHandler: async response => {
            const data = await response.json()

            return data // Need to specify this otherwise it tries to JSON.parse response, causing error
          }
        }
      },
      transformResponse: (response: SAPResponse) => {
        const transformed = response.items.row.map(row => ({ id: row.COST_OBJECT, name: `${row.COST_OBJECT} - ${row.DESCR}` }))
        return transformed
      }
    }),
    getSupervisors: builder.query<any[], Query>({
      query: (query: Query) => {
        const { selectedProfitCenters, search, financialPeriod } = query
        return {
          url: 'supervisors',
          method: 'POST',
          body: {
            profitCenters: selectedProfitCenters
          },
          params: { search: search.length > 0 ? query.search : undefined, financialPeriod: financialPeriod === 'No financial period' ? undefined : financialPeriod },
          responseHandler: async response => {
            const data = await response.json()
            return data // Need to specify this otherwise it tries to JSON.parse response, causing error
          }
        }
      },
      transformResponse: (response: SAPResponse) => {
        const transformed = response.item.row.map(row => ({ id: row.MITID, name: `${row.DESCR}` }))
        return transformed
      }
    }),
    getProfitCenters: builder.query<any[], string>({
      query: (searchString: string) => {
        return {
          url: 'profit-centers',
          method: 'GET',
          params: { search: searchString },
          responseHandler: async response => {
            const data = await response.json()
            return data // Need to specify this otherwise it tries to JSON.parse response, causing error
          }
        }
      },
      transformResponse: (response: SAPResponse) => {
        const transformed = response.item.row.map(row => ({ id: row.PRCTR, name: `${row.PRCTR} - ${row.DESCR}` }))
        return transformed
      }
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetAllCostObjectsQuery, useGetSupervisorsQuery, useGetProfitCentersQuery } = FinanceApi
/* eslint-enable */
